<script>
  import get from "lodash/get";
  import { tick } from "svelte";
  import { Link } from "svelte-routing";
  import { Drawing } from "drawing";
  import { hiddenLocations, hiddenPages } from "#src/stores/ui.js";

  import SidebarSection from "./SidebarSection.svelte";
  import CaretLeft from "@local/assets/icons/caret-left.svg";
  import CaretRight from "@local/assets/icons/caret-right.svg";
  import markColor from "@local/extensions/drawing/mark-color.js";
  import { selectedLocations, settingTriopsUrl as triopsUrl } from "#src/stores/ui.js";
  import { api } from "#src/api";
  import extension from "@local/extensions/utilities/extension.js";
  import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
  import decodeImage from "#src/extensions/dom/image-decode.js";
  import eb from "#src/extensions/event-bus.js";

  export let group;
  export let recordid;
  export let record;
  export let selectable = false;
  export let disabled;

  const markShapes = {
    item: "hexagon",
    collection: "rectangle",
    type: "circle",
    document_note: "rectangle",
  };

  let selectedLocation = 0;
  let canvas;
  let img;
  let ctx;
  let imageData;

  $: documents = $group.documents;
  $: locations = $group.locations;
  $: {
    recordid;
    selectedLocation = 0;
  }
  $: pages = getRecordLocs(locations, recordid);
  $: page = pages[selectedLocation];
  $: fetchImageData(page);
  $: prevLocation = pages[selectedLocation - 1];
  $: nextLocation = pages[selectedLocation + 1];
  $: src = imageSrc(page, imageData);
  $: {
    if (canvas) {
      ctx = canvas.getContext("2d");
    }
  }
  $: {
    recordid;
    selectedLocation;
    canvas && ctx && img && imageData && page && src && render();
  }

  function getRecordLocs(locations, recordid) {
    const rLocs = locations.order
      .filter((id) => {
        const loc = locations[id];
        return loc.record_id === recordid;
      })
      .map((id) => locations[id])
      .reduce((locs, loc) => {
        if (!locs[loc.document_id]) locs[loc.document_id] = [];
        locs[loc.document_id].push(loc);
        return locs;
      }, {});

    const docPages = Object.keys(rLocs).map((docid) => {
      const doc = documents[docid];
      const locs = rLocs[docid].reduce((pages, loc) => {
        if (!pages[loc.page_id]) pages[loc.page_id] = [];
        pages[loc.page_id].push(loc);
        return pages;
      }, {});
      if (!doc?.data?.pages) return [];

      const pages = Object.entries(locs)
        .map(([pageid, locs]) => {
          const page = doc.data.pages[pageid];
          if (!page) return null;
          return {
            document: doc,
            page,
            extension: extension(page?.name),
            locations: locs,
          };
        })
        .filter((p) => p);

      return pages;
    });

    return docPages.flat();
  }

  async function fetchImageData(page) {
    if (!page) return;

    if (Number.isFinite(page.page.page)) {
      if (page.extension === "pdf") {
        const path = encodeURIComponent(`${nameMinusExtension(page.page.name)}`);
        const res = await fetch(`${$triopsUrl}/iiif/2/drawings/${path}.${page.page.page}.tiff/info.json`);
        const data = await res.json();
        imageData = data;
      } else {
        const { data, error } = await api.storage.from("documents").createSignedUrl(page.page.name, 60);
        if (error) return;

        const img = await decodeImage(data.signedUrl);
        imageData = {
          src: data.signedUrl,
          width: img.width,
          height: img.height,
        };
      }
    } else {
      imageData = null;
    }
  }

  function imageSrc(page, imageData) {
    if (!page) return null;

    if (page.extension === "pdf") {
      const path = encodeURIComponent(`${nameMinusExtension(page.page.name)}`);
      return `${$triopsUrl}/iiif/2/drawings/${path}.${page.page.page}.tiff/full/540,/0/default.jpg`;
    } else if (imageData) {
      return imageData.src;
    }

    return null;
  }

  function gotoPrev() {
    selectedLocation -= 1;
  }

  function gotoNext() {
    selectedLocation += 1;
  }

  async function selectLocations(page) {
    const ids = page.locations
      .filter((l) => {
        const p = page.page;
        return !$hiddenLocations[l.id] && !$hiddenPages[p.id] && !p.hidden;
      })
      .map((l) => l.id);
    if (ids.length) {
      await tick();
      selectedLocations.selectOnly(...ids);
      eb.dispatch("zoom-to-page", page.page.id);
    }
  }

  async function render() {
    const width = imageData.width;
    const height = imageData.height;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (!img) return;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;

    const car = cw / ch;
    const iar = iw / ih;

    let zoom;
    let translate;
    if (car > iar) {
      // canvas.width = iw * (ch / ih);
      // canvas.height = ch;
      zoom = ch / height;
      translate = { x: (cw - width * zoom) / 2, y: 0 };
    } else {
      // canvas.width = cw;
      // canvas.height = ih * (cw / iw);
      zoom = cw / width;
      translate = { x: 0, y: (ch - height * zoom) / 2 };
    }

    canvas.width = cw;
    canvas.height = ch;

    ctx.setTransform(zoom, 0, 0, zoom, translate.x, translate.y);

    page.locations.forEach((l) => {
      const { x, y } = l.position;
      const pos = { x, y: -y };
      const labelBy = record.is_collection
        ? $group.data.settings.collections_label_by
        : $group.data.settings.label_by;
      const mark = labelBy ? get(record, labelBy) : record.mark;

      const dwg = new Drawing()
        .mark(pos, mark, {
          shape: markShapes[l.record_type],
        })
        .style(markColor(record, { emphasized: true }));

      dwg.render({ ctx, annoScale: 1 / zoom });
    });
  }
</script>

{#key recordid}
  <SidebarSection>
    <div class="flex justify-between items-center px-2">
      <h3 class="mb-2">Locations</h3>
      <div>
        <button
          class="prev-next-button"
          on:click={gotoPrev}
          disabled={!prevLocation}
          class:active={prevLocation}>
          <CaretLeft />
        </button>
        <button
          class="prev-next-button"
          on:click={gotoNext}
          disabled={!nextLocation}
          class:active={nextLocation}>
          <CaretRight />
        </button>
      </div>
    </div>
    <div class="space-y-2 px-2">
      {#if pages.length === 0}
        <div class="flex justify-between items-center">
          <div class="text-gray-400">No locations.</div>
          {#if !disabled}
            <Link to={"documents"}>
              <div>+ Add markers</div>
            </Link>
          {/if}
        </div>
      {:else}
        <div>
          <div class="border mb-1">
            <Link
              to={`documents/${page.document.id}?doc-page=${page.page.id}`}
              on:click={() => selectLocations(page)}>
              <div class="relative">
                <img
                  bind:this={img}
                  class="w-full h-36 object-contain text-gray-500 text-sm"
                  {src}
                  alt={page.document.name} />
                <!-- <div class="text-gray-500 text-xs p-2">Page not processed.</div> -->
                <div class="absolute top-0 left-0 w-full h-36">
                  <canvas bind:this={canvas} class="w-full h-full" />
                </div>
              </div>
            </Link>
          </div>
          <div class="flex justify-between items-center px-1">
            <div class="flex gap-1 grow overflow-hidden">
              <div class="truncate">{page.document.name}</div>
            </div>
            {#if pages.length > 0}
              <div class="flex-none">{selectedLocation + 1}/{pages.length}</div>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </SidebarSection>
{/key}

<style lang="scss">
  .prev-next-button {
    @apply rounded p-1 text-gray-400;

    &.active {
      @apply text-black;
      &:hover {
        @apply bg-gray-100;
      }
    }
  }
</style>
